// The billing panel on the company record (milestone 8 check 8; module 10 DESIGN "The billing panel
// on the company record"; module 04 D213; module 12 A17, DESIGN §9). Draws what the API read from
// the nightly copy of Anaplan through the billing customers a person confirmed, and computes
// nothing of its own beyond the chart's scale. Where the store has no copy or map (available is
// false) it draws NOTHING — no heading, no sentence — because hidden means hidden (R1). No variance
// line: finance's Forecast equals its actuals on every closed month, so the line would read zero for
// every client, healthy or broken.
import { ActionButton } from '../parts/index.js';
import type { BillingMonth, BillingTerm, CompanyBilling } from '../api.js';
import { dayMonthYear, dayMonthYearTime, monthShort } from '../day.js';
import './billing.css';
import { whose, type Billed, type MoneyLine } from './company-read';
import { staleWords } from './account';

function monthWord(period: string): string {
  const d = new Date(`${period}T00:00:00Z`);
  return `${monthShort(d, 'utc')} ${String(d.getUTCFullYear()).slice(-2)}`;
}
function dateWord(d: string | null): string {
  return d === null ? '—' : dayMonthYear(new Date(`${d}T00:00:00Z`), 'utc');
}
const pounds = (v: number | null): string => (v === null ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`);

function Chart({ months }: { months: BillingMonth[] }) {
  const W = 560; const H = 120; const pad = 4; const gap = 4;
  const bw = Math.max(6, (W - pad * 2 - gap * (months.length - 1)) / Math.max(1, months.length));
  const max = Math.max(1, ...months.map((m) => m.billed ?? m.forecast ?? 0));
  const y = (v: number): number => H - pad - (Math.max(0, v) / max) * (H - pad * 2 - 18);
  const firstForecast = months.findIndex((m) => m.billed === null);
  return (
    <svg className="billing-chart" viewBox={`0 0 ${W} ${H + 18}`} role="img" aria-label="billed by month, then forecast">
      {months.map((m, i) => {
        const x = pad + i * (bw + gap);
        const isForecast = m.billed === null;
        const v = isForecast ? (m.forecast ?? 0) : (m.billed ?? 0);
        const top = y(v);
        return (
          <g key={m.period}>
            <title>{`${monthWord(m.period)}: ${pounds(v)} ${isForecast ? 'forecast' : 'billed'}`}</title>
            <rect x={x} y={top} width={bw} height={Math.max(1, H - pad - top)} rx={2} className={isForecast ? 'billing-bar-forecast' : 'billing-bar'} />
            {(i === 0 || i === months.length - 1 || i === firstForecast - 1) ? <text x={x + bw / 2} y={H + 12} textAnchor="middle" className="billing-label">{monthWord(m.period)}</text> : null}
            {i === firstForecast ? <text x={x} y={12} className="billing-label">forecast</text> : null}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * What the twelve months are made of. The single figure above it is the whole reason this panel
 * was rebuilt: on its own it said £11,500 for a client whose signed register carries £480,000 a
 * month, and a reader had no way to see which product lines that figure did and did not cover.
 * Since BUGS.md T61 the lines are the company read's (U362: finance's leaf product lines, with the company's own share
 * of a line finance splits by operator), so they are exactly the lines the figure above is the sum of.
 */
function Lines({ lines, total }: { lines: MoneyLine[]; total: number | null }) {
  if (lines.length === 0) return null;
  const share = (n: number) => (total && total > 0 ? Math.round((n / total) * 100) : null);
  return (
    <div className="billing-lines">
      <table>
        <tbody>
          {lines.map((l) => {
            const pc = share(l.amount);
            return (
              <tr key={`${l.customer}|${l.product}`}>
                <th scope="row">{l.product}</th>
                <td>{pounds(l.amount)}</td>
                <td className="billing-share">{pc === null ? '' : `${pc}%`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="src">Finance&rsquo;s own product lines, over the same twelve months as the figure above.</p>
    </div>
  );
}

/**
 * The headline: what was billed in the last twelve months, as the company read has it (BUGS.md T61). There is one
 * billing figure everywhere, Mike's U362 one, so the panel no longer prints a sum of finance's "Total Revenue" roll-up
 * of its own (it read £957,775 for Bet365 beside the triage card's £956,780). Where the company read could not be made
 * the figure is said to be not read, never replaced by the other method's.
 */
export function billedWords(billed: Billed | null): { lead: string; figure: string | null; rest: string } {
  if (billed === null) return { lead: 'Billed over the last twelve months: not read, as finance’s figures for this company could not be read just now', figure: null, rest: '.' };
  if (billed.total === null) return { lead: 'Nothing billed over the last twelve months', figure: null, rest: '.' };
  const share = billed.words.label.replace(/^Billed, last 12 months/, '');
  return { lead: 'Billed', figure: pounds(billed.total), rest: ` over the last twelve months${billed.window ? ` to ${monthWord(billed.window.to)}` : ''}${share}.` };
}

/**
 * `billed` is the company read's billed money (CompanyRecord passes `c.read.money.billed`), or null where the read could
 * not be made: the headline and the product lines are its; the month chart stays finance's own months.
 */
export function BillingPanel({ billing, billed, name, isAdmin, open }: { billing: CompanyBilling; billed: Billed | null; name: string; isAdmin: boolean; open: (path: string) => void }) {
  // Nothing at all where the store has no copy or map, and nothing where there is not even a proposal a person could act on.
  if (!billing.available) return null;
  if (!billing.confirmed && billing.candidates === 0) return null;
  return (
    // BUGS.md D177: drawn as the record's other sections are, a head band and a body, with where it comes from said once.
    <section className="sec billing" id="a-billing">
      <div className="sh"><h2>Billing</h2></div>
      <div className="sb">
      {!billing.confirmed ? (
        <div className="billing-unconfirmed">
          <p className="gist">No billing until a person confirms which of finance&rsquo;s customers {name} is. A proposal is not a fact, and another client&rsquo;s revenue on this page is the one mistake that would end trust in it.</p>
          {isAdmin ? <ActionButton reading type="button" className="opt lead billing-confirm" onClick={() => open('/rails?tab=billing')}>Confirm it on the billing map</ActionButton> : <p className="src">An admin confirms it on the billing map.</p>}
        </div>
      ) : (
        <>
          <div className="billing-block" data-block="contract">
            <h3>The contract, as finance holds it</h3>
            {billing.terms.length === 0 ? <p className="gist">Finance carries no contract dates for this client&rsquo;s customers.</p> : (
              <table className="billing-terms">
                <thead><tr><th>Agreement</th><th>Term</th><th>To its end</th></tr></thead>
                <tbody>
                  {billing.terms.map((t, i) => {
                    const m = t.monthsTillContractEnd;
                    const ended = m !== null && m <= 0;
                    // BUGS.md D190: warm only for an agreement within 90 days of its end (VISUAL-SYSTEM.md), about three months.
                    const soon = m !== null && m > 0 && m <= 3;
                    return (
                      <tr key={`${t.customer}|${t.company}|${t.product}|${i}`} className={soon ? 'soon' : ended ? 'ended' : undefined}>
                        <td><b>{t.product ?? '—'}</b><div className="dim">{t.company ?? ''}{billing.customers.length > 1 ? `, ${t.customer}` : ''}</div><Whose t={t} name={name} open={open} /></td>
                        <td><span className="nw">{dateWord(t.contractStart)}</span> → <span className="nw">{dateWord(t.contractEnd)}</span></td>
                        <td>{m === null ? '' : ended ? 'ended' : `${Math.round(m)} months to end`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="billing-block" data-block="billed">
            <h3>Billed, by month</h3>
            {billing.months.length === 0 ? <p className="gist">No month on record for this client&rsquo;s customers yet.</p> : (
              <>
                <Chart months={billing.months} />
                {(() => { const w = billedWords(billed); return <p className="gist" data-figure="billed">{w.lead}{w.figure ? <> <b>{w.figure}</b></> : null}{w.rest}</p>; })()}
                {billed ? <Lines lines={billed.lines} total={billed.total} /> : null}
              </>
            )}
          </div>
          {(() => { const w = staleWords('The copy of finance\u2019s figures', billing.copiedAt, new Date(), dayMonthYearTime); return w ? <p className="stale" role="status">{w}</p> : null; })()}
          <p className="src">Source: finance&rsquo;s model as copied each night, its contract dates from the {billing.version} forecast, for finance&rsquo;s customer{billing.customers.length === 1 ? '' : 's'} {billing.customers.join(', ')}{billing.copiedAt ? `; figures copied ${dayMonthYearTime(new Date(billing.copiedAt))}` : ''}.</p>
          <WhoLinked links={billing.links} />
        </>
      )}
      </div>
    </section>
  );
}

/**
 * Whose agreement a finance term is (BUGS.md D161). Every term of every customer joined to the company was drawn as
 * the company's own: on Bet365's record finance's "Modus" customer brought the MODUS Online rights-holder agreement's
 * 1 Feb 2022 to 31 Jan 2027, in red, while Deals gave Bet365's own licence 30 June 2028, and nothing joined the two.
 * Each term now names the agreement it belongs to, and says so when that is another company's.
 */
export function whoseWords(t: Pick<BillingTerm, 'agreement'>, name: string): { text: string; other: { id: string; name: string } | null } {
  const a = t.agreement;
  if (a === undefined) return { text: '', other: null };
  if (a === null) return { text: `No agreement on ${whose(name)} register is joined to this term`, other: null };
  const title = a.title ?? 'agreement';
  if (a.ours) return { text: `${whose(name)} ${title}${a.by === 'dates' ? ', whose register dates are these' : ''}`, other: null };
  return { text: `Not ${whose(name)} own agreement: these are the dates of ${whose(a.organisationName)} ${title}${a.by === 'dates' ? ' on the register' : ''}`, other: { id: a.organisationId, name: a.organisationName } };
}

function Whose({ t, name, open }: { t: BillingTerm; name: string; open: (path: string) => void }) {
  const w = whoseWords(t, name);
  if (!w.text) return null;
  return (
    <div className="dim billing-whose">
      {w.text}
      {w.other ? <>; <ActionButton reading type="button" className="go" onClick={() => open(`/companies/${w.other!.id}`)}>open {w.other.name}</ActionButton></> : null}
    </div>
  );
}

/**
 * Who joined this company to its billing name, and on what (A37).
 *
 * Measured on the live store the day this was written: of 157 confirmed links, ONE was confirmed by
 * a person and 156 by Cobrain on a name match. 43 of those pointed at a company module 15 move 2 had
 * created FROM the billing name days before, and 36 of those companies held no domain, no person and
 * no contract. Matching a name to a company generated from that same name is a tautology, and it is
 * how £787,642 of Bet365's revenue came to sit on an empty page while its own record read £11,500.
 *
 * A figure is only as good as the join under it, so the panel says which kind of join it has. It
 * says nothing at all where a person made the decision, because there is nothing to warn about.
 */
function WhoLinked({ links }: { links: CompanyBilling['links'] }) {
  if (!links || links.length === 0) return null;
  const weak = links.filter((l) => l.confirmed && !l.byPerson && l.targetIsBare);
  const machine = links.filter((l) => l.confirmed && !l.byPerson && !l.targetIsBare);
  if (weak.length === 0 && machine.length === 0) return null;
  return (
    <p className="src linkwho">
      {weak.length ? (
        <><b>Nobody has checked this join.</b> Cobrain joined {weak.map((l) => `\u201c${l.customer}\u201d`).join(', ')} to this company on the name alone, and this record holds no mail domain, no person and no agreement to bear it out. A figure is only as good as the join under it.</>
      ) : (
        <>Cobrain joined {machine.map((l) => `\u201c${l.customer}\u201d`).join(', ')} to this company on the name; no person has confirmed it.</>
      )}
    </p>
  );
}
